import { Trash2 } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Invoice } from '@/pages/InvoicesPage.types'

export interface InvoiceDiscountCode {
  id: number
  code: string
  percent: number | null
  fixed_amount?: string | null
  availability: string
}

export function InvoiceDiscountCodeCard({
  invoice,
  codes,
  loading,
  failed,
  selectedCode,
  pending,
  error,
  onSelect,
  onApply,
  onRemove,
}: {
  invoice: Invoice
  codes: InvoiceDiscountCode[]
  loading: boolean
  failed: boolean
  selectedCode: string
  pending: boolean
  error: Error | null
  onSelect: (code: string) => void
  onApply: () => void
  onRemove: () => void
}) {
  const errorMessage = apiFieldMessages(error, 'discount_code')[0]

  return (
    <section className="space-y-3 px-4 py-3">
      <h2 className="text-body font-semibold text-foreground">Discount Code</h2>
      {invoice.discount_row ? (
        <>
          <div className="flex items-baseline gap-2 text-body-sm">
            <span className="font-medium text-foreground">{invoice.discount_row.label}</span>
            <span className="tabular-nums text-muted-foreground">
              {invoice.discount_row.reduction}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="quietDestructive"
            disabled={pending || !invoice.discount_code_change_available}
            onClick={onRemove}
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
            Remove Discount Code
          </Button>
          {invoice.discount_code_change_unavailable_reason && (
            <p className="text-caption text-muted-foreground">
              {invoice.discount_code_change_unavailable_reason}
            </p>
          )}
        </>
      ) : loading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading Discount Codes...
        </p>
      ) : failed ? (
        <Alert>Discount Codes unavailable</Alert>
      ) : codes.length === 0 ? (
        <p className="text-body-sm text-muted-foreground">No Discount Codes available.</p>
      ) : (
        <>
          <Label htmlFor="invoice-discount-code">Discount Code</Label>
          <Select value={selectedCode} onValueChange={onSelect}>
            <SelectTrigger id="invoice-discount-code" aria-label="Discount Code">
              <SelectValue placeholder="Choose a Discount Code" />
            </SelectTrigger>
            <SelectContent>
              {codes.map((code) => (
                <SelectItem key={code.id} value={code.code}>
                  {code.code} · {code.percent === null ? code.fixed_amount : `${code.percent}%`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pending || selectedCode.length === 0}
            onClick={onApply}
          >
            Apply Discount Code
          </Button>
        </>
      )}
      {errorMessage && <Alert>{errorMessage}</Alert>}
    </section>
  )
}
